import { gql, useQuery } from "@apollo/client";
import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { ICustomerQuery } from "@repo/shopify/queries/customer-gql";
import type { IShopQuery } from "@repo/shopify/queries/others-gql";
import { shopQuery } from "@repo/shopify/queries/others-gql";
import type { DOMNode, Element, HTMLReactParserOptions } from "html-react-parser";
import parse, { attributesToProps, domToReact } from "html-react-parser";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";

import Button from "@atoms/Button";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

const SUB_PAGES = [
  { title: "Contact", id: "contact", url: "contact" },
  { title: "Membership", id: "membership", url: "membership" },
  { title: "Shipping and Returns", id: "shipping-and-returns", url: "shipping-and-returns" },
];

const MEMBERSHIP_TABLE = [
  { membership: "Membership", benefits: "Benefits", purchase_amount: "Purchase Amount\n(Recent 3 Years)" },
  {
    membership: "Member",
    benefits: t("account.membership.member.benefits"),
    purchase_amount: t("account.membership.member.purchase_amount"),
  },
  {
    membership: "Silver",
    benefits: t("account.membership.silver.benefits"),
    purchase_amount: t("account.membership.silver.purchase_amount"),
  },
  {
    membership: "Gold",
    benefits: t("account.membership.gold.benefits"),
    purchase_amount: t("account.membership.gold.purchase_amount"),
  },
  {
    membership: "Platinum",
    benefits: t("account.membership.platinum.benefits"),
    purchase_amount: t("account.membership.platinum.purchase_amount"),
  },
  {
    membership: "VIP",
    benefits: t("account.membership.vip.benefits"),
    purchase_amount: t("account.membership.vip.purchase_amount"),
  },
];

export default function Information() {
  const navigate = useNavigate();
  const location = useLocation();

  const { openToast, openAccount, showFooter } = useLayoutStore();
  const { isPC } = useCheckDevice();
  const { lang } = useLanguage();

  const { customer } = useCustomerStore();

  const [information, setInformation] = useState("contact");

  const { data, loading } = useQuery<IShopQuery>(gql(shopQuery.query), {
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  useEffect(() => {
    showFooter(true);
    window.scrollTo(0, 0);
  }, []);

  const handleMembership = (_accessToken?: string, fetchedCustomer?: ICustomerQuery["customer"]) => {
    const _customer = fetchedCustomer ?? customer;
    if (!_customer?.id) {
      sessionStorage.setItem("loginAfterUrl", URLS.ACCOUNT);
      openAccount(true);
      return;
    }

    navigate(URLS.ACCOUNT);
  };

  useEffect(() => {
    if (!isPC) {
      const id = location.hash.replace("#", "") || "contact";
      if (id) {
        setInformation(id);
        setTimeout(() => {
          scrollToElement(id);
        }, 300);
      }
    }
  }, [location.hash, isPC]);

  const scrollToElement = (id: string) => {
    const imageElement = document.getElementById(id);
    const elementPosition = imageElement?.getBoundingClientRect();
    const offsetPosition = (elementPosition?.top || 0) + window.scrollY - 60;
    window.scrollTo({ top: id === "contact" ? 0 : offsetPosition, behavior: "smooth" });
  };

  useEffect(() => {
    if (!isPC) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const index = entry.target.getAttribute("data-index");
            if (index !== null) {
              const idx = parseInt(index, 10);
              if (!SUB_PAGES[idx]) return;
            }
          });
        },
        {
          root: null, // Use viewport as root
          rootMargin: "0px",
          threshold: 0.1, // Trigger when 10% of the element is visible
        },
      );

      SUB_PAGES.forEach((item) => {
        const element = document.getElementById(item.id);
        if (element) observer.observe(element);
      });

      return () => {
        SUB_PAGES.forEach((item) => {
          const element = document.getElementById(item.id);
          if (element) observer.unobserve(element);
        });
      };
    }
  }, [data, information, isPC]);

  return (
    <>
      {loading ? (
        <Loading type="page" />
      ) : (
        <section
          className="tnt-gap-x pc:col-span-12 pc:grid pc:grid-cols-3 pc:gap-y-5 col-span-8 flex flex-col gap-y-20"
          id="information"
        >
          {/* Contact */}
          <article
            className="pc:col-span-1 col-span-8 flex h-fit flex-col gap-5 break-words"
            data-index={0}
            id="contact"
          >
            <h2 className="text-title">Contact</h2>
            <section className="flex flex-col gap-2.5">
              <span>Customer Care</span>
              <div className="tnt-gap-x pc:grid-cols-4 grid grid-cols-6 gap-y-1">
                <span className="pc:col-span-1 col-span-2">{t("email")}</span>
                <Link as="text" className="pc:col-span-3 col-span-4 w-fit" href={`mailto:${t("information.email")}`}>
                  {t("information.email")}
                </Link>
                {lang !== "jp" && (
                  <>
                    <span className="pc:col-span-1 col-span-2">Chat</span>
                    <Link
                      as="text"
                      className="pc:col-span-3 col-span-4 w-fit"
                      href={`https://${t("information.channel_talk")}`}
                    >
                      {t("information.channel_talk")}
                    </Link>
                  </>
                )}
                <span className="pc:col-span-1 col-span-2">{t("information.hours")}</span>
                <span className="pc:col-span-3 col-span-4">{t("information.cs_time")}</span>
              </div>
              {lang !== "jp" ? (
                <>
                  <span>{t("contact.cs_info")}</span>
                  <p>
                    <Trans
                      components={{
                        account: (
                          <Button
                            as="text"
                            className="inline!"
                            onClick={() => {
                              handleMembership();
                            }}
                          />
                        ),
                      }}
                      i18nKey="contact.description"
                    />
                  </p>
                </>
              ) : (
                <p>
                  {t("information.inquiry_notice")}
                  <br />
                  {t("information.tracking_notice")}
                  <br />
                  <a className="pc:hover:text-blue !inline disabled:cursor-not-allowed" href="/account" type="button">
                    {t("information.account_link_text")}
                  </a>
                </p>
              )}
            </section>
            {lang !== "jp" && (
              <section className="flex flex-col gap-2.5">
                <span>Wholesale & Inquiries</span>
                <div className="tnt-gap-x pc:grid-cols-4 grid grid-cols-6 gap-y-1">
                  <span className="pc:col-span-1 col-span-2">Email</span>
                  <Link as="text" className="pc:col-span-3 col-span-4 w-fit" href="mailto:info@thisisneverthat.com">
                    info@thisisneverthat.com
                  </Link>
                </div>
              </section>
            )}
          </article>

          {/* Membership */}
          {lang !== "jp" && (
            <article className="pc:col-span-1 col-span-8 flex h-fit flex-col gap-5" data-index={1} id="membership">
              <h2 className="text-title">Membership</h2>
              <SimpleBar autoHide={false}>
                <div className="tnt-gap-x pc:min-w-0 grid min-w-[450px] grid-cols-4 gap-y-5">
                  {MEMBERSHIP_TABLE.map((item) => (
                    <React.Fragment key={item.membership}>
                      <span>{item.membership}</span>
                      <div className="tnt-gap-x col-span-3 grid grid-cols-2">
                        <span className="whitespace-pre-wrap">{item.benefits}</span>
                        <span className="whitespace-pre-wrap">{item.purchase_amount}</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </SimpleBar>
              <Button
                as="text"
                onClick={() => {
                  handleMembership();
                }}
              >
                {t("account.membership.view_membership_detail")}
              </Button>
              <section className="flex flex-col gap-2.5">
                <span>Description</span>
                <ul className="font-features-normal flex list-disc flex-col gap-1 pl-4">
                  <li>{t("account.membership.policy.1")}</li>
                  <li>{t("account.membership.policy.2")}</li>
                  <li>{t("account.membership.policy.3")}</li>
                  <li>{t("account.membership.policy.4")}</li>
                  <li>{t("account.membership.policy.5")}</li>
                  <li>{t("account.membership.policy.6")}</li>
                  <li>{t("account.membership.policy.7")}</li>
                  <li>{t("account.membership.policy.8")}</li>
                  <li>{t("account.membership.policy.9")}</li>
                  <li>{t("account.membership.policy.10")}</li>
                  {lang === "en" && <li>{t("account.membership.policy.11")}</li>}
                </ul>
              </section>
            </article>
          )}

          {/* Shipping & Return */}
          <article
            className="pc:col-span-1 col-span-8 flex h-fit flex-col gap-5 break-words"
            data-index={lang !== "jp" ? 2 : 1}
            id="shipping-and-returns"
          >
            <h2 className="text-title">Shipping and Returns</h2>
            <ParseContent html={data?.shop.shippingPolicy?.body || ""} />
          </article>

          {/* FAQ */}
          {lang === "jp" && (
            <article className="pc:col-span-1 col-span-8 flex h-fit flex-col gap-5 break-words" data-index={2} id="faq">
              <h2 className="text-title">{t("information.qa.title")}</h2>
              <section className="flex flex-col gap-1">
                <span>{t("information.qa.payment_method_change_q")}</span>
                <span>{t("information.qa.payment_method_change_a")}</span>
              </section>
              <section className="flex flex-col gap-1">
                <span>{t("information.qa.no_confirmation_email_q")}</span>
                <span>{t("information.qa.no_confirmation_email_a")}</span>
              </section>
              <p>{t("information.qa.terms_notice")}</p>
            </article>
          )}
        </section>
      )}
    </>
  );
}

function ParseContent({ html }: { html: string }) {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      const domParse = domNode as Element;

      if (domParse?.name === "a") {
        const linkText = domToReact(domParse.children as DOMNode[], options) as JSX.Element;
        return (
          <Link as="text" {...attributesToProps(domParse.attribs)} className={domParse.attribs.class} to="">
            {linkText}
          </Link>
        );
      }

      if (domParse?.name === "button") {
        const buttonText = domToReact(domParse.children as DOMNode[], options) as JSX.Element;
        return <Button className={domParse?.attribs.class}>{buttonText}</Button>;
      }

      if (domParse.name === "h1" || domParse.name === "h2" || domParse.name === "h3" || domParse.name === "h4") {
        const headingText = domToReact(domParse.children as DOMNode[], options) as React.JSX.Element;
        const ParseDom = domParse.name;
        return <ParseDom className={domParse.attribs.class}>{headingText}</ParseDom>;
      }
    },
  };

  return <>{parse(html, options)}</>;
}
